import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Check, BookOpen, Eye, Wrench, Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  LEARN_CARDS, WITNESS_ITEMS, PRACTICE_ITEMS, SEVEN_UNDERSTANDINGS, CAPSTONE_KEY,
  type AttestationRow, type AttestationStage,
} from "./pathContent";

interface Props {
  successorId: string;
  successorName: string | null;
  associationName: string | null;
  officerRole: string | null;
}

type ItemMeta = { stage: AttestationStage; key: string };

export function SuccessorPath({ successorId, successorName, associationName, officerRole }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [rows, setRows] = useState<Record<string, AttestationRow>>({});
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [chapter, setChapter] = useState<"learn" | "witness" | "practice" | "capstone">("learn");

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("succession_attestations")
      .select("*")
      .eq("successor_id", successorId);
    if (error) {
      toast.error("Could not load your path. Please refresh.");
      setLoading(false);
      return;
    }
    const map: Record<string, AttestationRow> = {};
    const d: Record<string, string> = {};
    for (const r of (data ?? []) as AttestationRow[]) {
      const k = `${r.stage}:${r.item_key}`;
      map[k] = r;
      if (r.successor_response) d[k] = r.successor_response;
    }
    setRows(map);
    setDraft((prev) => ({ ...d, ...prev }));
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [successorId]);

  async function saveItem(meta: ItemMeta, opts?: { markConfirmed?: boolean; responseOverride?: string }) {
    const composite = `${meta.stage}:${meta.key}`;
    const response = opts?.responseOverride ?? draft[composite] ?? "";
    if (!response.trim() && meta.stage !== "learn" || (meta.stage === "learn" && !response.trim() && meta.key !== CAPSTONE_KEY)) {
      // Do nothing on empty saves.
    }
    setSaving(composite);
    try {
      const payload: any = {
        successor_id: successorId,
        stage: meta.stage,
        item_key: meta.key,
        successor_response: response || null,
        updated_at: new Date().toISOString(),
      };
      if (opts?.markConfirmed) payload.successor_confirmed_at = new Date().toISOString();
      const { error } = await supabase
        .from("succession_attestations")
        .upsert(payload, { onConflict: "successor_id,stage,item_key" });
      if (error) throw error;
      await load();
    } catch (e: any) {
      toast.error("We could not save that just now. Please try again.");
    } finally {
      setSaving(null);
    }
  }

  const learnDone = LEARN_CARDS.every((c) => rows[`learn:${c.key}`]?.successor_confirmed_at);
  const witnessDone = WITNESS_ITEMS.every((c) => rows[`witness:${c.key}`]?.successor_confirmed_at);
  const practiceDone = PRACTICE_ITEMS.every((c) => rows[`practice:${c.key}`]?.successor_confirmed_at);
  const capstoneDone = !!rows[`learn:${CAPSTONE_KEY}`]?.successor_confirmed_at;
  const progress = useMemo(() => {
    const total = LEARN_CARDS.length + WITNESS_ITEMS.length + PRACTICE_ITEMS.length + 1;
    const done =
      LEARN_CARDS.filter((c) => rows[`learn:${c.key}`]?.successor_confirmed_at).length +
      WITNESS_ITEMS.filter((c) => rows[`witness:${c.key}`]?.successor_confirmed_at).length +
      PRACTICE_ITEMS.filter((c) => rows[`practice:${c.key}`]?.successor_confirmed_at).length +
      (capstoneDone ? 1 : 0);
    return Math.round((done / total) * 100);
  }, [rows, capstoneDone]);

  if (loading) {
    return (
      <div className="min-h-[300px] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const tabs: { id: typeof chapter; label: string; icon: any; done: boolean; disabled?: boolean }[] = [
    { id: "learn", label: "Learn", icon: BookOpen, done: learnDone },
    { id: "witness", label: "Witness", icon: Eye, done: witnessDone },
    { id: "practice", label: "Practice", icon: Wrench, done: practiceDone },
    { id: "capstone", label: "Capstone", icon: Sparkles, done: capstoneDone, disabled: !(learnDone && witnessDone && practiceDone) },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground">Getting ready to serve as {officerRole || "a steward"} for</p>
        <h1 className="text-2xl md:text-3xl font-serif">{associationName || "your Association"}</h1>
        <p className="text-sm text-muted-foreground">Welcome, {successorName || "friend"}. This is your path.</p>
      </div>

      <div className="rounded-md border p-3 flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
        <span className="text-xs text-muted-foreground w-10 text-right">{progress}%</span>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        A few words as you begin. A <em>resolution</em> is a short paper where the Board (the group of officers) writes down a choice. <em>Minutes</em> are the notes from a meeting. You will meet these words again as you go.
      </p>

      <div className="grid grid-cols-4 gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            disabled={t.disabled}
            onClick={() => setChapter(t.id)}
            className={`rounded-md border p-3 text-left transition ${
              chapter === t.id ? "border-primary bg-primary/5" : "hover:border-primary/40"
            } ${t.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <div className="flex items-center gap-2">
              <t.icon className="h-4 w-4" />
              <span className="text-sm font-medium">{t.label}</span>
              {t.done && <Check className="h-3.5 w-3.5 text-primary ml-auto" />}
            </div>
          </button>
        ))}
      </div>

      {chapter === "learn" && (
        <div className="space-y-4">
          <h2 className="text-xl font-serif">Chapter one. Learn.</h2>
          <p className="text-sm text-muted-foreground">
            Seven short reads. Take each one, then say it back in your own words. Your own words help it stick.
          </p>
          {LEARN_CARDS.map((c) => {
            const composite = `learn:${c.key}`;
            const row = rows[composite];
            const confirmed = !!row?.successor_confirmed_at;
            return (
              <Card key={c.key} className={`p-5 space-y-3 ${confirmed ? "border-primary/50 bg-primary/5" : ""}`}>
                <div className="flex items-start justify-between">
                  <h3 className="font-serif text-lg">{c.title}</h3>
                  {confirmed && <span className="text-xs text-primary flex items-center gap-1"><Check className="h-3 w-3" /> Saved</span>}
                </div>
                <div className="space-y-2 text-sm">
                  {c.body.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <div className="space-y-2 pt-2">
                  <label className="text-sm font-medium">{c.prompt}</label>
                  <Textarea
                    rows={3}
                    value={draft[composite] ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, [composite]: e.target.value }))}
                    placeholder="Your words here."
                  />
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => saveItem({ stage: "learn", key: c.key }, { markConfirmed: true })}
                      disabled={saving === composite || !(draft[composite] ?? "").trim()}
                    >
                      {saving === composite && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                      {confirmed ? "Update" : "Save and continue"}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {chapter === "witness" && (
        <ChapterSimple
          title="Chapter two. Witness."
          intro="Three real moments to share with the steward. Do the thing. Then tell us here in a few words."
          items={WITNESS_ITEMS}
          stage="witness"
          rows={rows}
          draft={draft}
          setDraft={setDraft}
          saveItem={saveItem}
          saving={saving}
        />
      )}

      {chapter === "practice" && (
        <ChapterSimple
          title="Chapter three. Practice."
          intro="Three practice moments. You do the work together and leave a short note behind."
          items={PRACTICE_ITEMS}
          stage="practice"
          rows={rows}
          draft={draft}
          setDraft={setDraft}
          saveItem={saveItem}
          saving={saving}
        />
      )}

      {chapter === "capstone" && (
        <Capstone
          rows={rows}
          saveItem={saveItem}
          saving={saving}
        />
      )}

      <div className="flex justify-between pt-4">
        <Button variant="ghost" size="sm" disabled={chapter === "learn"} onClick={() => {
          const order: any[] = ["learn", "witness", "practice", "capstone"];
          setChapter(order[Math.max(0, order.indexOf(chapter) - 1)]);
        }}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Button variant="ghost" size="sm" disabled={chapter === "capstone"} onClick={() => {
          const order: any[] = ["learn", "witness", "practice", "capstone"];
          setChapter(order[Math.min(order.length - 1, order.indexOf(chapter) + 1)]);
        }}>
          Next <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function ChapterSimple({ title, intro, items, stage, rows, draft, setDraft, saveItem, saving }: {
  title: string;
  intro: string;
  items: { key: string; title: string; body: string; prompt: string }[];
  stage: "witness" | "practice";
  rows: Record<string, AttestationRow>;
  draft: Record<string, string>;
  setDraft: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  saveItem: (meta: ItemMeta, opts?: { markConfirmed?: boolean; responseOverride?: string }) => Promise<void>;
  saving: string | null;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-serif">{title}</h2>
      <p className="text-sm text-muted-foreground">{intro}</p>
      {items.map((c) => {
        const composite = `${stage}:${c.key}`;
        const row = rows[composite];
        const confirmed = !!row?.successor_confirmed_at;
        const chairConfirmed = !!row?.chair_confirmed_at;
        return (
          <Card key={c.key} className={`p-5 space-y-3 ${confirmed ? "border-primary/50 bg-primary/5" : ""}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-serif text-lg">{c.title}</h3>
              {chairConfirmed ? (
                <span className="text-xs text-primary flex items-center gap-1"><Check className="h-3 w-3" /> Marked done by your steward</span>
              ) : confirmed ? (
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Check className="h-3 w-3" /> Saved</span>
              ) : null}
            </div>
            <p className="text-sm">{c.body}</p>
            <div className="space-y-2 pt-2">
              <label className="text-sm font-medium">{c.prompt}</label>
              <Textarea
                rows={3}
                value={draft[composite] ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, [composite]: e.target.value }))}
                placeholder="A short note is enough."
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => saveItem({ stage, key: c.key }, { markConfirmed: true })}
                  disabled={saving === composite || !(draft[composite] ?? "").trim()}
                >
                  {saving === composite && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                  {confirmed ? "Update" : "Save and continue"}
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function Capstone({ rows, saveItem, saving }: {
  rows: Record<string, AttestationRow>;
  saveItem: (meta: ItemMeta, opts?: { markConfirmed?: boolean; responseOverride?: string }) => Promise<void>;
  saving: string | null;
}) {
  const composite = `learn:${CAPSTONE_KEY}`;
  const row = rows[composite];
  const existing = row?.successor_response ? tryParseArray(row.successor_response) : [];
  const [checked, setChecked] = useState<boolean[]>(() =>
    SEVEN_UNDERSTANDINGS.map((_, i) => existing.includes(i))
  );
  const allChecked = checked.every(Boolean);
  const already = !!row?.successor_confirmed_at;

  async function commit() {
    const acked = checked.map((c, i) => (c ? i : -1)).filter((n) => n >= 0);
    const responseJson = JSON.stringify({ acknowledged_indexes: acked, affirmations: SEVEN_UNDERSTANDINGS });
    await saveItem({ stage: "learn", key: CAPSTONE_KEY }, { markConfirmed: true, responseOverride: responseJson });
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-serif">Capstone. The Seven Promises.</h2>
      <p className="text-sm text-muted-foreground">
        Read each one with care. Check the box only when you can say yes to it, plainly and with no pressure.
      </p>
      <Card className={`p-5 space-y-4 ${already ? "border-primary/60 bg-primary/5" : ""}`}>
        {SEVEN_UNDERSTANDINGS.map((s, i) => (
          <label key={i} className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={checked[i]}
              onCheckedChange={(v) => {
                const next = [...checked];
                next[i] = !!v;
                setChecked(next);
              }}
              className="mt-1"
            />
            <span className="text-sm leading-relaxed">{i + 1}. {s}</span>
          </label>
        ))}
        <div className="pt-2 flex justify-end">
          <Button onClick={commit} disabled={!allChecked || saving === composite}>
            {saving === composite && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {already ? "Update my answer" : "I say yes to all seven"}
          </Button>
        </div>
      </Card>
      {already && (
        <Card className="p-6 text-center space-y-2 border-primary/50 bg-primary/5">
          <Sparkles className="h-8 w-8 text-primary mx-auto" />
          <h3 className="font-serif text-lg">Thank you.</h3>
          <p className="text-sm text-muted-foreground">
            Your steward can now read your path and mark the shared moments as done. You will hear from them next.
          </p>
        </Card>
      )}
    </div>
  );
}

function tryParseArray(s: string): number[] {
  try {
    const v = JSON.parse(s);
    if (Array.isArray(v?.acknowledged_indexes)) return v.acknowledged_indexes;
  } catch {}
  return [];
}
